import type {
  OffsetPageDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import {
  type BrandPrintLanguage,
  withBrandPrintLanguage,
} from "@/shared/lib/brand-document-print-language";
import { fetcher } from "@/shared/lib/fetcher";
import type {
  AssignmentDTO,
  BankAccountTemplateDTO,
  AddEventShowToAllAssignmentsDTO,
  BulkCreateAssignmentsDTO,
  BulkUpdateAssignmentShowsDTO,
  BulkUpdateAssignmentShowsResultDTO,
  EventShowDeleteImpactDTO,
  PartialShowParticipationDTO,
  BulkCreateAssignmentsResultDTO,
  BulkCreateRehearsalSessionsDTO,
  BulkRescheduleRehearsalSessionsDTO,
  BulkUpdateRehearsalSessionStatusDTO,
  CancelPayoutDTO,
  CommissionDTO,
  ProductionCostLineDTO,
  ProductionCostListResponseDTO,
  ConvertRequestToEventDTO,
  CreateAssignmentDTO,
  CreateBankAccountTemplateDTO,
  CreateCommissionDTO,
  BulkCreateEventOutsourcesFromStaffDTO,
  BulkCreateEventOutsourcesFromStaffResultDTO,
  BulkCreateOutsourcesFromStaffDTO,
  BulkCreateOutsourcesFromStaffResultDTO,
  CreateEventOutsourceDTO,
  CreateOutsourceCategoryDTO,
  CreateOutsourceDTO,
  AdminRecalcEventOutsourceAgreedAmountDTO,
  CreateOutsourceApInstallmentPlanTemplateDTO,
  CreateOutsourcePayoutDTO,
  EventOutsourceInstallmentDTO,
  SaveEventOutsourceInstallmentPlanDTO,
  OutsourceApInstallmentPlanTemplateDTO,
  UpdateOutsourceApInstallmentPlanTemplateDTO,
  CreateProductionCostLineDTO,
  CreateDocumentFromParentDTO,
  CreateDraftQuotationDTO,
  CreateEventDTO,
  AssignEventStaffDTO,
  CreateInstallmentPlanTemplateDTO,
  CreateManualLineDTO,
  CreatePaymentReceiptDTO,
  CreatePaymentTermsTemplateDTO,
  CreatePayoutDTO,
  CreateStaffDTO,
  CreateProductionPayoutDTO,
  CreateStaffPayoutDTO,
  CreatePerDiemPolicyDTO,
  CreatePerformerDTO,
  CreateRehearsalSessionDTO,
  CreateRequestDTO,
  DocumentDTO,
  DocumentLineDTO,
  DocumentListItemDTO,
  EventDateChangeDTO,
  EventDTO,
  EventDetailReportDTO,
  GoogleCalendarSyncStatusDTO,
  EventEditFormDTO,
  EventFxRatesDTO,
  EventRehearsalSummaryDTO,
  EventShowDTO,
  EventOutsourceDTO,
  EventStaffDTO,
  CreateEventCategoryDTO,
  CreateEventShowDTO,
  UpdateEventShowDTO,
  EventCategoryDTO,
  EventCategoryLookupItemDTO,
  EventCategoryLookupQueryDTO,
  EventCalendarQueryDTO,
  EventCalendarResponseDTO,
  EventDateConflictQueryDTO,
  EventDateConflictResponseDTO,
  EventTeamAssignmentWarningsCountDTO,
  RequestCalendarQueryDTO,
  RequestCalendarResponseDTO,
  InstallmentPlanTemplateDTO,
  InvoicePaymentSummaryDTO,
  IssueDocumentDTO,
  MarkPayoutPaidDTO,
  PaymentTermsTemplateDTO,
  PayoutDTO,
  PayoutListItemDTO,
  PerDiemPolicyDTO,
  PerformerDTO,
  PostponeEventDTO,
  OutsourceCategoryDTO,
  OutsourceCategoryLookupItemDTO,
  OutsourceCategoryLookupQueryDTO,
  OutsourceDTO,
  OutsourceListQueryDTO,
  OutsourceLookupQueryDTO,
  StaffDTO,
  StaffLookupQueryDTO,
  PostponeEventResultDTO,
  PricingSyncResultDTO,
  QuotationInstallmentPlanDTO,
  RehearsalSessionDTO,
  RequestDTO,
  RoleFeeConfigDTO,
  SetPayoutPaymentProofDTO,
  TaxSettingsDTO,
  UpdateAssignmentDTO,
  UpdateBankAccountTemplateDTO,
  UpdateCommissionDTO,
  UpdateProductionCostLineDTO,
  UpdateDocumentBankAccountDTO,
  UpdateDocumentCurrencyDTO,
  UpdateDocumentPaymentTermsDTO,
  UpdateDocumentTaxDTO,
  UpdateDocumentBillDiscountDTO,
  UpdateEventCategoryDTO,
  UpdateEventDTO,
  UpdateEventFxRatesDTO,
  UpdateEventOutsourceDTO,
  UpdateEventStaffAssignmentDTO,
  UpdateOutsourceCategoryDTO,
  UpdateOutsourceDTO,
  UpdateInstallmentPlanDTO,
  UpdateInstallmentPlanTemplateDTO,
  UpdateManualLineDTO,
  UpdatePaymentReceiptDraftDTO,
  UpdatePaymentTermsTemplateDTO,
  UpdatePayoutDTO,
  UpdatePerDiemPolicyDTO,
  UpdatePerformerDTO,
  UpdateStaffDTO,
  UpdateRehearsalSessionDTO,
  UpdateRequestDTO,
  UpdateRoleFeeConfigDTO,
  UpdateTaxSettingsDTO,
  VoidDocumentDTO,
} from "../../domain/contracts";
import type { PerformanceOperationsDashboardDTO } from "../../domain/contracts/dashboard";
import type {
  PerformanceEventReportDTO,
  PerformanceEventReportQueryDTO,
  PerformancePayoutReportDTO,
  PerformancePayoutReportQueryDTO,
  PerformancePerformerReportDTO,
  PerformancePerformerReportQueryDTO,
  PerformanceReportSummaryDTO,
  PerformanceReportSummaryQueryDTO,
  PerformanceRequestReportDTO,
  PerformanceRequestReportQueryDTO,
  PerformanceRevenueReportDTO,
  PerformanceRevenueReportQueryDTO,
} from "../../domain/contracts/reports";

function buildListUrl(base: string, query: OffsetPageQueryDTO): string {
  const url = new URL(base);
  url.searchParams.set("limit", String(query.limit ?? 20));
  url.searchParams.set("offset", String(query.offset ?? 0));
  if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
  if (query.filters)
    url.searchParams.set("filters", JSON.stringify(query.filters));
  return url.toString();
}

function buildReportUrl(
  base: string,
  query: Record<string, string | number | undefined>,
): string {
  const url = new URL(base);
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== "") {
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

export const performanceApi = {
  requests: {
    async list(query: OffsetPageQueryDTO): Promise<OffsetPageDTO<RequestDTO>> {
      return fetcher.get<OffsetPageDTO<RequestDTO>>(
        buildListUrl(`${config.apiUrl}/requests`, query),
      );
    },

    async getCalendar(
      query: RequestCalendarQueryDTO,
    ): Promise<RequestCalendarResponseDTO> {
      const url = new URL(`${config.apiUrl}/requests/calendar`);
      url.searchParams.set("start", query.start);
      url.searchParams.set("end", query.end);
      if (query.status) url.searchParams.set("status", query.status);
      if (query.title) url.searchParams.set("title", query.title);
      if (query.channel) url.searchParams.set("channel", query.channel);
      if (query.assignedTo) url.searchParams.set("assignedTo", query.assignedTo);
      if (query.priorityOnly) url.searchParams.set("priorityOnly", "true");
      return fetcher.get<RequestCalendarResponseDTO>(url.toString());
    },

    async get(id: string): Promise<RequestDTO> {
      return fetcher.get<RequestDTO>(`${config.apiUrl}/requests/${id}`);
    },

    async create(input: CreateRequestDTO): Promise<RequestDTO> {
      return fetcher.post<RequestDTO>(`${config.apiUrl}/requests`, input);
    },

    async update(id: string, input: UpdateRequestDTO): Promise<RequestDTO> {
      return fetcher.patch<RequestDTO>(
        `${config.apiUrl}/requests/${id}`,
        input,
      );
    },

    async remove(id: string): Promise<{ deleted: boolean }> {
      return fetcher.delete<{ deleted: boolean }>(
        `${config.apiUrl}/requests/${id}`,
      );
    },

    async googleCalendar(id: string): Promise<GoogleCalendarSyncStatusDTO> {
      return fetcher.get<GoogleCalendarSyncStatusDTO>(
        `${config.apiUrl}/requests/${id}/google-calendar`,
      );
    },

    async syncGoogleCalendar(id: string): Promise<{ ok: true }> {
      return fetcher.post<{ ok: true }>(
        `${config.apiUrl}/requests/${id}/google-calendar/sync`,
      );
    },

    async convertToEvent(
      id: string,
      input: ConvertRequestToEventDTO,
    ): Promise<{
      request: RequestDTO;
      event: EventDTO;
      copiedAssignments: number;
    }> {
      return fetcher.post<{
        request: RequestDTO;
        event: EventDTO;
        copiedAssignments: number;
      }>(`${config.apiUrl}/requests/${id}/convert-to-event`, input);
    },
  },

  performers: {
    async list(
      query: OffsetPageQueryDTO,
    ): Promise<OffsetPageDTO<PerformerDTO>> {
      return fetcher.get<OffsetPageDTO<PerformerDTO>>(
        buildListUrl(`${config.apiUrl}/performers`, query),
      );
    },

    async get(id: string): Promise<PerformerDTO> {
      return fetcher.get<PerformerDTO>(`${config.apiUrl}/performers/${id}`);
    },

    async create(input: CreatePerformerDTO): Promise<PerformerDTO> {
      return fetcher.post<PerformerDTO>(`${config.apiUrl}/performers`, input);
    },

    async update(id: string, input: UpdatePerformerDTO): Promise<PerformerDTO> {
      return fetcher.patch<PerformerDTO>(
        `${config.apiUrl}/performers/${id}`,
        input,
      );
    },

    async remove(id: string): Promise<{ deleted: boolean }> {
      return fetcher.delete<{ deleted: boolean }>(
        `${config.apiUrl}/performers/${id}`,
      );
    },
  },

  outsourceCategories: {
    async list(): Promise<{ data: OutsourceCategoryDTO[] }> {
      return fetcher.get<{ data: OutsourceCategoryDTO[] }>(
        `${config.apiUrl}/outsource-categories`,
      );
    },

    async lookup(
      query: OutsourceCategoryLookupQueryDTO,
    ): Promise<{ items: OutsourceCategoryLookupItemDTO[]; total: number }> {
      const params = new URLSearchParams();
      if (query.q) params.set("q", query.q);
      params.set("limit", String(query.limit));
      params.set("skip", String(query.skip));
      return fetcher.get<{ items: OutsourceCategoryLookupItemDTO[]; total: number }>(
        `${config.apiUrl}/outsource-categories/lookup?${params.toString()}`,
      );
    },

    async create(input: CreateOutsourceCategoryDTO): Promise<OutsourceCategoryDTO> {
      return fetcher.post<OutsourceCategoryDTO>(
        `${config.apiUrl}/outsource-categories`,
        input,
      );
    },

    async update(
      id: string,
      input: UpdateOutsourceCategoryDTO,
    ): Promise<OutsourceCategoryDTO> {
      return fetcher.patch<OutsourceCategoryDTO>(
        `${config.apiUrl}/outsource-categories/${id}`,
        input,
      );
    },
  },

  outsources: {
    async list(
      query: OutsourceListQueryDTO,
    ): Promise<{ data: OutsourceDTO[]; meta: { total: number } }> {
      const params = new URLSearchParams();
      if (query.q) params.set("q", query.q);
      if (query.categoryId) params.set("categoryId", query.categoryId);
      if (query.payeeType) params.set("payeeType", query.payeeType);
      if (query.status) params.set("status", query.status);
      params.set("limit", String(query.limit));
      params.set("skip", String(query.skip));
      const qs = params.toString();
      return fetcher.get<{ data: OutsourceDTO[]; meta: { total: number } }>(
        `${config.apiUrl}/outsources${qs ? `?${qs}` : ""}`,
      );
    },

    async get(id: string): Promise<OutsourceDTO> {
      return fetcher.get<OutsourceDTO>(`${config.apiUrl}/outsources/${id}`);
    },

    async create(input: CreateOutsourceDTO): Promise<OutsourceDTO> {
      return fetcher.post<OutsourceDTO>(`${config.apiUrl}/outsources`, input);
    },

    async bulkCreateFromStaff(
      input: BulkCreateOutsourcesFromStaffDTO,
    ): Promise<BulkCreateOutsourcesFromStaffResultDTO> {
      return fetcher.post<BulkCreateOutsourcesFromStaffResultDTO>(
        `${config.apiUrl}/outsources/bulk-from-staff`,
        input,
      );
    },

    async update(id: string, input: UpdateOutsourceDTO): Promise<OutsourceDTO> {
      return fetcher.patch<OutsourceDTO>(
        `${config.apiUrl}/outsources/${id}`,
        input,
      );
    },
  },

  staff: {
    async list(
      query: StaffLookupQueryDTO,
    ): Promise<{ data: StaffDTO[]; meta: { total: number } }> {
      const params = new URLSearchParams();
      if (query.q) params.set("q", query.q);
      params.set("limit", String(query.limit));
      params.set("skip", String(query.skip));
      const qs = params.toString();
      return fetcher.get<{ data: StaffDTO[]; meta: { total: number } }>(
        `${config.apiUrl}/staff${qs ? `?${qs}` : ""}`,
      );
    },

    async get(id: string): Promise<StaffDTO> {
      return fetcher.get<StaffDTO>(`${config.apiUrl}/staff/${id}`);
    },

    async create(input: CreateStaffDTO): Promise<StaffDTO> {
      return fetcher.post<StaffDTO>(`${config.apiUrl}/staff`, input);
    },

    async update(id: string, input: UpdateStaffDTO): Promise<StaffDTO> {
      return fetcher.patch<StaffDTO>(`${config.apiUrl}/staff/${id}`, input);
    },

    async remove(id: string): Promise<StaffDTO | { deleted: boolean }> {
      return fetcher.delete<StaffDTO | { deleted: boolean }>(
        `${config.apiUrl}/staff/${id}`,
      );
    },
  },

  paymentTermsTemplates: {
    async list(
      query: OffsetPageQueryDTO,
    ): Promise<OffsetPageDTO<PaymentTermsTemplateDTO>> {
      return fetcher.get<OffsetPageDTO<PaymentTermsTemplateDTO>>(
        buildListUrl(`${config.apiUrl}/payment-terms-templates`, query),
      );
    },

    async lookup(
      documentType?: "quotation" | "invoice",
      taxVariant?: "without_tax" | "with_tax",
    ) {
      const params = new URLSearchParams();
      if (documentType) params.set("documentType", documentType);
      if (taxVariant) params.set("taxVariant", taxVariant);
      const qs = params.toString();
      return fetcher.get<{ items: PaymentTermsTemplateDTO[] }>(
        `${config.apiUrl}/payment-terms-templates/lookup${qs ? `?${qs}` : ""}`,
      );
    },

    async get(id: string): Promise<PaymentTermsTemplateDTO> {
      return fetcher.get<PaymentTermsTemplateDTO>(
        `${config.apiUrl}/payment-terms-templates/${id}`,
      );
    },

    async create(
      input: CreatePaymentTermsTemplateDTO,
    ): Promise<PaymentTermsTemplateDTO> {
      return fetcher.post<PaymentTermsTemplateDTO>(
        `${config.apiUrl}/payment-terms-templates`,
        input,
      );
    },

    async update(
      id: string,
      input: UpdatePaymentTermsTemplateDTO,
    ): Promise<PaymentTermsTemplateDTO> {
      return fetcher.patch<PaymentTermsTemplateDTO>(
        `${config.apiUrl}/payment-terms-templates/${id}`,
        input,
      );
    },

    async remove(id: string): Promise<{ deleted: boolean }> {
      return fetcher.delete<{ deleted: boolean }>(
        `${config.apiUrl}/payment-terms-templates/${id}`,
      );
    },
  },

  bankAccountTemplates: {
    async list(
      query: OffsetPageQueryDTO,
    ): Promise<OffsetPageDTO<BankAccountTemplateDTO>> {
      return fetcher.get<OffsetPageDTO<BankAccountTemplateDTO>>(
        buildListUrl(`${config.apiUrl}/bank-account-templates`, query),
      );
    },

    async lookup(taxVariant?: "without_tax" | "with_tax") {
      const params = new URLSearchParams();
      if (taxVariant) params.set("taxVariant", taxVariant);
      const qs = params.toString();
      return fetcher.get<{ items: BankAccountTemplateDTO[] }>(
        `${config.apiUrl}/bank-account-templates/lookup${qs ? `?${qs}` : ""}`,
      );
    },

    async get(id: string): Promise<BankAccountTemplateDTO> {
      return fetcher.get<BankAccountTemplateDTO>(
        `${config.apiUrl}/bank-account-templates/${id}`,
      );
    },

    async create(
      input: CreateBankAccountTemplateDTO,
    ): Promise<BankAccountTemplateDTO> {
      return fetcher.post<BankAccountTemplateDTO>(
        `${config.apiUrl}/bank-account-templates`,
        input,
      );
    },

    async update(
      id: string,
      input: UpdateBankAccountTemplateDTO,
    ): Promise<BankAccountTemplateDTO> {
      return fetcher.patch<BankAccountTemplateDTO>(
        `${config.apiUrl}/bank-account-templates/${id}`,
        input,
      );
    },

    async remove(id: string): Promise<{ deleted: boolean }> {
      return fetcher.delete<{ deleted: boolean }>(
        `${config.apiUrl}/bank-account-templates/${id}`,
      );
    },
  },

  installmentPlanTemplates: {
    async list(
      query: OffsetPageQueryDTO,
    ): Promise<OffsetPageDTO<InstallmentPlanTemplateDTO>> {
      return fetcher.get<OffsetPageDTO<InstallmentPlanTemplateDTO>>(
        buildListUrl(`${config.apiUrl}/installment-plan-templates`, query),
      );
    },

    async lookup() {
      return fetcher.get<{ items: InstallmentPlanTemplateDTO[] }>(
        `${config.apiUrl}/installment-plan-templates/lookup`,
      );
    },

    async get(id: string): Promise<InstallmentPlanTemplateDTO> {
      return fetcher.get<InstallmentPlanTemplateDTO>(
        `${config.apiUrl}/installment-plan-templates/${id}`,
      );
    },

    async create(
      input: CreateInstallmentPlanTemplateDTO,
    ): Promise<InstallmentPlanTemplateDTO> {
      return fetcher.post<InstallmentPlanTemplateDTO>(
        `${config.apiUrl}/installment-plan-templates`,
        input,
      );
    },

    async update(
      id: string,
      input: UpdateInstallmentPlanTemplateDTO,
    ): Promise<InstallmentPlanTemplateDTO> {
      return fetcher.patch<InstallmentPlanTemplateDTO>(
        `${config.apiUrl}/installment-plan-templates/${id}`,
        input,
      );
    },

    async remove(id: string): Promise<{ deleted: boolean }> {
      return fetcher.delete<{ deleted: boolean }>(
        `${config.apiUrl}/installment-plan-templates/${id}`,
      );
    },
  },

  outsourceApInstallmentPlanTemplates: {
    async list(
      query: OffsetPageQueryDTO,
    ): Promise<OffsetPageDTO<OutsourceApInstallmentPlanTemplateDTO>> {
      return fetcher.get<OffsetPageDTO<OutsourceApInstallmentPlanTemplateDTO>>(
        buildListUrl(
          `${config.apiUrl}/outsource-ap-installment-plan-templates`,
          query,
        ),
      );
    },

    async lookup() {
      return fetcher.get<{ items: OutsourceApInstallmentPlanTemplateDTO[] }>(
        `${config.apiUrl}/outsource-ap-installment-plan-templates/lookup`,
      );
    },

    async get(id: string): Promise<OutsourceApInstallmentPlanTemplateDTO> {
      return fetcher.get<OutsourceApInstallmentPlanTemplateDTO>(
        `${config.apiUrl}/outsource-ap-installment-plan-templates/${id}`,
      );
    },

    async create(
      input: CreateOutsourceApInstallmentPlanTemplateDTO,
    ): Promise<OutsourceApInstallmentPlanTemplateDTO> {
      return fetcher.post<OutsourceApInstallmentPlanTemplateDTO>(
        `${config.apiUrl}/outsource-ap-installment-plan-templates`,
        input,
      );
    },

    async update(
      id: string,
      input: UpdateOutsourceApInstallmentPlanTemplateDTO,
    ): Promise<OutsourceApInstallmentPlanTemplateDTO> {
      return fetcher.patch<OutsourceApInstallmentPlanTemplateDTO>(
        `${config.apiUrl}/outsource-ap-installment-plan-templates/${id}`,
        input,
      );
    },

    async remove(id: string): Promise<{ deleted: boolean }> {
      return fetcher.delete<{ deleted: boolean }>(
        `${config.apiUrl}/outsource-ap-installment-plan-templates/${id}`,
      );
    },
  },

  perDiemPolicies: {
    async list(
      query: OffsetPageQueryDTO,
    ): Promise<OffsetPageDTO<PerDiemPolicyDTO>> {
      return fetcher.get<OffsetPageDTO<PerDiemPolicyDTO>>(
        buildListUrl(`${config.apiUrl}/per-diem-policies`, query),
      );
    },

    async lookup() {
      return fetcher.get<{
        items: Array<{ id: string; code: string; name: string }>;
      }>(`${config.apiUrl}/per-diem-policies/lookup`);
    },

    async get(id: string): Promise<PerDiemPolicyDTO> {
      return fetcher.get<PerDiemPolicyDTO>(
        `${config.apiUrl}/per-diem-policies/${id}`,
      );
    },

    async create(input: CreatePerDiemPolicyDTO): Promise<PerDiemPolicyDTO> {
      return fetcher.post<PerDiemPolicyDTO>(
        `${config.apiUrl}/per-diem-policies`,
        input,
      );
    },

    async update(
      id: string,
      input: UpdatePerDiemPolicyDTO,
    ): Promise<PerDiemPolicyDTO> {
      return fetcher.patch<PerDiemPolicyDTO>(
        `${config.apiUrl}/per-diem-policies/${id}`,
        input,
      );
    },

    async remove(id: string): Promise<{ deleted: boolean }> {
      return fetcher.delete<{ deleted: boolean }>(
        `${config.apiUrl}/per-diem-policies/${id}`,
      );
    },
  },

  eventCategories: {
    async list(): Promise<{ data: EventCategoryDTO[] }> {
      return fetcher.get<{ data: EventCategoryDTO[] }>(
        `${config.apiUrl}/event-categories`,
      );
    },

    async lookup(query?: EventCategoryLookupQueryDTO) {
      const params = new URLSearchParams();
      if (query?.q) params.set("q", query.q);
      const qs = params.toString();
      return fetcher.get<{
        items: EventCategoryLookupItemDTO[];
        total: number;
      }>(
        `${config.apiUrl}/event-categories/lookup${qs ? `?${qs}` : ""}`,
      );
    },

    async get(id: string): Promise<EventCategoryDTO> {
      return fetcher.get<EventCategoryDTO>(
        `${config.apiUrl}/event-categories/${id}`,
      );
    },

    async create(input: CreateEventCategoryDTO): Promise<EventCategoryDTO> {
      return fetcher.post<EventCategoryDTO>(
        `${config.apiUrl}/event-categories`,
        input,
      );
    },

    async update(
      id: string,
      input: UpdateEventCategoryDTO,
    ): Promise<EventCategoryDTO> {
      return fetcher.patch<EventCategoryDTO>(
        `${config.apiUrl}/event-categories/${id}`,
        input,
      );
    },

    async remove(id: string): Promise<{ deleted: boolean }> {
      return fetcher.delete<{ deleted: boolean }>(
        `${config.apiUrl}/event-categories/${id}`,
      );
    },
  },

  roleFeeConfigs: {
    async list(): Promise<{ items: RoleFeeConfigDTO[] }> {
      return fetcher.get<{ items: RoleFeeConfigDTO[] }>(
        `${config.apiUrl}/role-fee-configs`,
      );
    },

    async update(
      roleFeeKind: RoleFeeConfigDTO["roleFeeKind"],
      input: UpdateRoleFeeConfigDTO,
    ): Promise<RoleFeeConfigDTO> {
      return fetcher.patch<RoleFeeConfigDTO>(
        `${config.apiUrl}/role-fee-configs/${roleFeeKind}`,
        input,
      );
    },
  },

  events: {
    async countTeamAssignmentWarnings(): Promise<EventTeamAssignmentWarningsCountDTO> {
      return fetcher.get<EventTeamAssignmentWarningsCountDTO>(
        `${config.apiUrl}/events/team-assignment-warnings/count`,
      );
    },

    async list(query: OffsetPageQueryDTO): Promise<OffsetPageDTO<EventDTO>> {
      return fetcher.get<OffsetPageDTO<EventDTO>>(
        buildListUrl(`${config.apiUrl}/events`, query),
      );
    },

    async getCalendar(
      query: EventCalendarQueryDTO,
    ): Promise<EventCalendarResponseDTO> {
      const url = new URL(`${config.apiUrl}/events/calendar`);
      url.searchParams.set("start", query.start);
      url.searchParams.set("end", query.end);
      url.searchParams.set("layer", query.layer ?? "events");
      if (query.status) url.searchParams.set("status", query.status);
      if (query.categoryId) url.searchParams.set("categoryId", query.categoryId);
      if (query.title) url.searchParams.set("title", query.title);
      return fetcher.get<EventCalendarResponseDTO>(url.toString());
    },

    async checkDateConflicts(
      query: EventDateConflictQueryDTO,
    ): Promise<EventDateConflictResponseDTO> {
      const url = new URL(`${config.apiUrl}/events/date-conflicts`);
      url.searchParams.set("eventDate", query.eventDate);
      if (query.endDate) url.searchParams.set("endDate", query.endDate);
      if (query.excludeEventId) {
        url.searchParams.set("excludeEventId", query.excludeEventId);
      }
      if (query.excludeRequestId) {
        url.searchParams.set("excludeRequestId", query.excludeRequestId);
      }
      return fetcher.get<EventDateConflictResponseDTO>(url.toString());
    },

    async get(
      id: string,
    ): Promise<EventDTO & { assignments: AssignmentDTO[] }> {
      return fetcher.get<EventDTO & { assignments: AssignmentDTO[] }>(
        `${config.apiUrl}/events/${id}`,
      );
    },

    async getForEdit(id: string): Promise<EventEditFormDTO> {
      return fetcher.get<EventEditFormDTO>(
        `${config.apiUrl}/events/${id}/edit-form`,
      );
    },

    async googleCalendar(id: string): Promise<GoogleCalendarSyncStatusDTO> {
      return fetcher.get<GoogleCalendarSyncStatusDTO>(
        `${config.apiUrl}/events/${id}/google-calendar`,
      );
    },

    async syncGoogleCalendar(id: string): Promise<{ ok: true }> {
      return fetcher.post<{ ok: true }>(
        `${config.apiUrl}/events/${id}/google-calendar/sync`,
      );
    },

    async report(eventId: string): Promise<EventDetailReportDTO> {
      return fetcher.get<EventDetailReportDTO>(
        `${config.apiUrl}/events/${eventId}/report`,
      );
    },

    async updateFxRates(
      eventId: string,
      input: UpdateEventFxRatesDTO,
    ): Promise<EventFxRatesDTO> {
      return fetcher.patch<EventFxRatesDTO>(
        `${config.apiUrl}/events/${eventId}/fx-rates`,
        input,
      );
    },

    async create(input: CreateEventDTO): Promise<EventDTO> {
      return fetcher.post<EventDTO>(`${config.apiUrl}/events`, input);
    },

    async update(id: string, input: UpdateEventDTO): Promise<EventDTO> {
      return fetcher.patch<EventDTO>(`${config.apiUrl}/events/${id}`, input);
    },

    async remove(id: string): Promise<{ deleted: boolean }> {
      return fetcher.delete<{ deleted: boolean }>(
        `${config.apiUrl}/events/${id}`,
      );
    },

    async postpone(
      id: string,
      input: PostponeEventDTO,
    ): Promise<PostponeEventResultDTO> {
      return fetcher.post<PostponeEventResultDTO>(
        `${config.apiUrl}/events/${id}/postpone`,
        input,
      );
    },

    async dateChanges(id: string): Promise<{ items: EventDateChangeDTO[] }> {
      return fetcher.get<{ items: EventDateChangeDTO[] }>(
        `${config.apiUrl}/events/${id}/date-changes`,
      );
    },

    assignments: {
      async list(eventId: string): Promise<AssignmentDTO[]> {
        return fetcher.get<AssignmentDTO[]>(
          `${config.apiUrl}/events/${eventId}/assignments`,
        );
      },

      async create(
        eventId: string,
        input: CreateAssignmentDTO,
      ): Promise<AssignmentDTO> {
        return fetcher.post<AssignmentDTO>(
          `${config.apiUrl}/events/${eventId}/assignments`,
          input,
        );
      },

      async update(
        eventId: string,
        assignmentId: string,
        input: UpdateAssignmentDTO,
      ): Promise<AssignmentDTO> {
        return fetcher.patch<AssignmentDTO>(
          `${config.apiUrl}/events/${eventId}/assignments/${assignmentId}`,
          input,
        );
      },

      async remove(
        eventId: string,
        assignmentId: string,
      ): Promise<{ deleted: boolean }> {
        return fetcher.delete<{ deleted: boolean }>(
          `${config.apiUrl}/events/${eventId}/assignments/${assignmentId}`,
        );
      },

      async bulkCreate(
        eventId: string,
        input: BulkCreateAssignmentsDTO,
      ): Promise<BulkCreateAssignmentsResultDTO> {
        return fetcher.post<BulkCreateAssignmentsResultDTO>(
          `${config.apiUrl}/events/${eventId}/assignments/bulk`,
          input,
        );
      },

      async bulkUpdateShows(
        eventId: string,
        input: BulkUpdateAssignmentShowsDTO,
      ): Promise<BulkUpdateAssignmentShowsResultDTO> {
        return fetcher.post<BulkUpdateAssignmentShowsResultDTO>(
          `${config.apiUrl}/events/${eventId}/assignments/bulk-update-shows`,
          input,
        );
      },

      async addShowToAll(
        eventId: string,
        input: AddEventShowToAllAssignmentsDTO,
      ): Promise<{ updated: number; skipped: number }> {
        return fetcher.post<{ updated: number; skipped: number }>(
          `${config.apiUrl}/events/${eventId}/assignments/add-show-to-all`,
          input,
        );
      },

      async partialShowParticipation(
        eventId: string,
      ): Promise<PartialShowParticipationDTO[]> {
        return fetcher.get<PartialShowParticipationDTO[]>(
          `${config.apiUrl}/events/${eventId}/partial-show-participation`,
        );
      },
    },

    showRosterPrintUrl(eventId: string, showId?: string): string {
      const base = `${config.apiUrl}/events/${eventId}/show-roster/print`;
      return showId ? `${base}?showId=${encodeURIComponent(showId)}` : base;
    },

    async rehearsalSummary(eventId: string): Promise<EventRehearsalSummaryDTO> {
      return fetcher.get<EventRehearsalSummaryDTO>(
        `${config.apiUrl}/events/${eventId}/rehearsal-summary`,
      );
    },

    shows: {
      async list(eventId: string): Promise<EventShowDTO[]> {
        return fetcher.get<EventShowDTO[]>(
          `${config.apiUrl}/events/${eventId}/shows`,
        );
      },
      async create(
        eventId: string,
        input: CreateEventShowDTO,
      ): Promise<EventShowDTO> {
        return fetcher.post<EventShowDTO>(
          `${config.apiUrl}/events/${eventId}/shows`,
          input,
        );
      },
      async update(
        eventId: string,
        showId: string,
        input: UpdateEventShowDTO,
      ): Promise<EventShowDTO> {
        return fetcher.patch<EventShowDTO>(
          `${config.apiUrl}/events/${eventId}/shows/${showId}`,
          input,
        );
      },
      async remove(
        eventId: string,
        showId: string,
      ): Promise<{ deleted: boolean }> {
        return fetcher.delete<{ deleted: boolean }>(
          `${config.apiUrl}/events/${eventId}/shows/${showId}`,
        );
      },
      async deleteImpact(
        eventId: string,
        showId: string,
      ): Promise<EventShowDeleteImpactDTO> {
        return fetcher.get<EventShowDeleteImpactDTO>(
          `${config.apiUrl}/events/${eventId}/shows/${showId}/delete-impact`,
        );
      },
    },

    rehearsalSessions: {
      printUrl(
        eventId: string,
        lang: BrandPrintLanguage = "lo",
        options?: { layout?: "day" | "matrix" },
      ): string {
        return withBrandPrintLanguage(
          `${config.apiUrl}/events/${eventId}/rehearsal-sessions/print`,
          lang,
          options,
        );
      },
      async list(eventId: string): Promise<RehearsalSessionDTO[]> {
        return fetcher.get<RehearsalSessionDTO[]>(
          `${config.apiUrl}/events/${eventId}/rehearsal-sessions`,
        );
      },
      async create(
        eventId: string,
        input: CreateRehearsalSessionDTO,
      ): Promise<RehearsalSessionDTO> {
        return fetcher.post<RehearsalSessionDTO>(
          `${config.apiUrl}/events/${eventId}/rehearsal-sessions`,
          input,
        );
      },
      async bulkCreate(
        eventId: string,
        input: BulkCreateRehearsalSessionsDTO,
      ): Promise<RehearsalSessionDTO[]> {
        return fetcher.post<RehearsalSessionDTO[]>(
          `${config.apiUrl}/events/${eventId}/rehearsal-sessions/bulk`,
          input,
        );
      },
      async bulkReschedule(
        eventId: string,
        input: BulkRescheduleRehearsalSessionsDTO,
      ): Promise<RehearsalSessionDTO[]> {
        return fetcher.patch<RehearsalSessionDTO[]>(
          `${config.apiUrl}/events/${eventId}/rehearsal-sessions/bulk-reschedule`,
          input,
        );
      },
      async bulkUpdateStatus(
        eventId: string,
        input: BulkUpdateRehearsalSessionStatusDTO,
      ): Promise<RehearsalSessionDTO[]> {
        return fetcher.patch<RehearsalSessionDTO[]>(
          `${config.apiUrl}/events/${eventId}/rehearsal-sessions/bulk-status`,
          input,
        );
      },
      async update(
        eventId: string,
        sessionId: string,
        input: UpdateRehearsalSessionDTO,
      ): Promise<RehearsalSessionDTO> {
        return fetcher.patch<RehearsalSessionDTO>(
          `${config.apiUrl}/events/${eventId}/rehearsal-sessions/${sessionId}`,
          input,
        );
      },
      async remove(
        eventId: string,
        sessionId: string,
      ): Promise<{ deleted: boolean }> {
        return fetcher.delete<{ deleted: boolean }>(
          `${config.apiUrl}/events/${eventId}/rehearsal-sessions/${sessionId}`,
        );
      },
    },

    documents: {
      async list(eventId: string): Promise<DocumentDTO[]> {
        return fetcher.get<DocumentDTO[]>(
          `${config.apiUrl}/events/${eventId}/documents`,
        );
      },

      async createDraft(
        eventId: string,
        input: CreateDraftQuotationDTO = {},
      ): Promise<DocumentDTO> {
        return fetcher.post<DocumentDTO>(
          `${config.apiUrl}/events/${eventId}/documents`,
          input,
        );
      },

      async syncFromAssignments(
        eventId: string,
        documentId: string,
      ): Promise<PricingSyncResultDTO> {
        return fetcher.post<PricingSyncResultDTO>(
          `${config.apiUrl}/events/${eventId}/documents/${documentId}/sync-from-assignments`,
          {},
        );
      },

      async updateCurrency(
        eventId: string,
        documentId: string,
        input: UpdateDocumentCurrencyDTO,
      ): Promise<DocumentDTO> {
        return fetcher.patch<DocumentDTO>(
          `${config.apiUrl}/events/${eventId}/documents/${documentId}/currency`,
          input,
        );
      },

      async issue(
        eventId: string,
        documentId: string,
        input: IssueDocumentDTO = {},
      ): Promise<DocumentDTO> {
        return fetcher.post<DocumentDTO>(
          `${config.apiUrl}/events/${eventId}/documents/${documentId}/issue`,
          input,
        );
      },

      async reopen(eventId: string, documentId: string): Promise<DocumentDTO> {
        return fetcher.post<DocumentDTO>(
          `${config.apiUrl}/events/${eventId}/documents/${documentId}/reopen`,
          {},
        );
      },

      async void(
        eventId: string,
        documentId: string,
        input: VoidDocumentDTO = {},
      ): Promise<DocumentDTO> {
        return fetcher.post<DocumentDTO>(
          `${config.apiUrl}/events/${eventId}/documents/${documentId}/void`,
          input,
        );
      },

      async remove(
        eventId: string,
        documentId: string,
      ): Promise<{ deleted: boolean }> {
        return fetcher.delete<{ deleted: boolean }>(
          `${config.apiUrl}/events/${eventId}/documents/${documentId}`,
        );
      },

      async createFromParent(
        eventId: string,
        input: CreateDocumentFromParentDTO,
      ): Promise<DocumentDTO> {
        return fetcher.post<DocumentDTO>(
          `${config.apiUrl}/events/${eventId}/documents/from-parent`,
          input,
        );
      },

      async createPaymentReceipt(
        eventId: string,
        invoiceId: string,
        input: CreatePaymentReceiptDTO,
      ): Promise<DocumentDTO> {
        return fetcher.post<DocumentDTO>(
          `${config.apiUrl}/events/${eventId}/invoices/${invoiceId}/receipts`,
          input,
        );
      },

      async getPaymentSummary(
        eventId: string,
        invoiceId: string,
      ): Promise<InvoicePaymentSummaryDTO> {
        return fetcher.get<InvoicePaymentSummaryDTO>(
          `${config.apiUrl}/events/${eventId}/invoices/${invoiceId}/payment-summary`,
        );
      },

      async updateInstallmentPlan(
        eventId: string,
        invoiceId: string,
        input: UpdateInstallmentPlanDTO,
      ): Promise<InvoicePaymentSummaryDTO> {
        return fetcher.put<InvoicePaymentSummaryDTO>(
          `${config.apiUrl}/events/${eventId}/invoices/${invoiceId}/installments`,
          input,
        );
      },

      async getQuotationInstallmentPlan(
        eventId: string,
        quotationId: string,
      ): Promise<QuotationInstallmentPlanDTO> {
        return fetcher.get<QuotationInstallmentPlanDTO>(
          `${config.apiUrl}/events/${eventId}/quotations/${quotationId}/installments`,
        );
      },

      async updateQuotationInstallmentPlan(
        eventId: string,
        quotationId: string,
        input: UpdateInstallmentPlanDTO,
      ): Promise<QuotationInstallmentPlanDTO> {
        return fetcher.put<QuotationInstallmentPlanDTO>(
          `${config.apiUrl}/events/${eventId}/quotations/${quotationId}/installments`,
          input,
        );
      },

      async updatePaymentReceiptDraft(
        eventId: string,
        documentId: string,
        input: UpdatePaymentReceiptDraftDTO,
      ): Promise<DocumentDTO> {
        return fetcher.patch<DocumentDTO>(
          `${config.apiUrl}/events/${eventId}/documents/${documentId}/payment-receipt`,
          input,
        );
      },

      async updatePaymentTerms(
        eventId: string,
        documentId: string,
        input: UpdateDocumentPaymentTermsDTO,
      ): Promise<DocumentDTO> {
        return fetcher.patch<DocumentDTO>(
          `${config.apiUrl}/events/${eventId}/documents/${documentId}/payment-terms`,
          input,
        );
      },

      async updateBankAccount(
        eventId: string,
        documentId: string,
        input: UpdateDocumentBankAccountDTO,
      ): Promise<DocumentDTO> {
        return fetcher.patch<DocumentDTO>(
          `${config.apiUrl}/events/${eventId}/documents/${documentId}/bank-account`,
          input,
        );
      },

      async updateTax(
        eventId: string,
        documentId: string,
        input: UpdateDocumentTaxDTO,
      ): Promise<DocumentDTO> {
        return fetcher.patch<DocumentDTO>(
          `${config.apiUrl}/events/${eventId}/documents/${documentId}/tax`,
          input,
        );
      },

      async updateBillDiscount(
        eventId: string,
        documentId: string,
        input: UpdateDocumentBillDiscountDTO,
      ): Promise<DocumentDTO> {
        return fetcher.patch<DocumentDTO>(
          `${config.apiUrl}/events/${eventId}/documents/${documentId}/bill-discount`,
          input,
        );
      },
    },

    payouts: {
      async list(eventId: string): Promise<PayoutListItemDTO[]> {
        return fetcher.get<PayoutListItemDTO[]>(
          `${config.apiUrl}/events/${eventId}/payouts`,
        );
      },

      async create(
        eventId: string,
        input: CreatePayoutDTO,
      ): Promise<{ payout: PayoutDTO; warnings: unknown[] }> {
        return fetcher.post<{ payout: PayoutDTO; warnings: unknown[] }>(
          `${config.apiUrl}/events/${eventId}/payouts`,
          input,
        );
      },

      async createStaff(
        eventId: string,
        input: CreateStaffPayoutDTO,
      ): Promise<{ payout: PayoutDTO; warnings: unknown[] }> {
        return fetcher.post<{ payout: PayoutDTO; warnings: unknown[] }>(
          `${config.apiUrl}/events/${eventId}/payouts/staff`,
          input,
        );
      },

      async createProduction(
        eventId: string,
        input: CreateProductionPayoutDTO,
      ): Promise<{ payout: PayoutDTO; warnings: unknown[] }> {
        return fetcher.post<{ payout: PayoutDTO; warnings: unknown[] }>(
          `${config.apiUrl}/events/${eventId}/payouts/production`,
          input,
        );
      },

      async remove(
        eventId: string,
        payoutId: string,
      ): Promise<{ deleted: boolean }> {
        return fetcher.delete<{ deleted: boolean }>(
          `${config.apiUrl}/events/${eventId}/payouts/${payoutId}`,
        );
      },
    },

    staff: {
      async list(
        eventId: string,
        query?: { activeOnly?: boolean },
      ): Promise<EventStaffDTO[]> {
        const params = new URLSearchParams();
        if (query?.activeOnly) params.set("activeOnly", "true");
        const qs = params.toString();
        return fetcher.get<EventStaffDTO[]>(
          `${config.apiUrl}/events/${eventId}/staff${qs ? `?${qs}` : ""}`,
        );
      },

      async assign(
        eventId: string,
        input: AssignEventStaffDTO,
      ): Promise<EventStaffDTO> {
        return fetcher.post<EventStaffDTO>(
          `${config.apiUrl}/events/${eventId}/staff`,
          input,
        );
      },

      async update(
        eventId: string,
        assignmentId: string,
        input: UpdateEventStaffAssignmentDTO,
      ): Promise<EventStaffDTO> {
        return fetcher.patch<EventStaffDTO>(
          `${config.apiUrl}/events/${eventId}/staff/${assignmentId}`,
          input,
        );
      },

      async remove(
        eventId: string,
        assignmentId: string,
      ): Promise<EventStaffDTO | { deleted: boolean }> {
        return fetcher.delete<EventStaffDTO | { deleted: boolean }>(
          `${config.apiUrl}/events/${eventId}/staff/${assignmentId}`,
        );
      },
    },

    commissions: {
      async list(eventId: string): Promise<CommissionDTO[]> {
        return fetcher.get<CommissionDTO[]>(
          `${config.apiUrl}/events/${eventId}/commissions`,
        );
      },

      async create(
        eventId: string,
        input: CreateCommissionDTO,
      ): Promise<CommissionDTO> {
        return fetcher.post<CommissionDTO>(
          `${config.apiUrl}/events/${eventId}/commissions`,
          input,
        );
      },

      async update(
        eventId: string,
        commissionId: string,
        input: UpdateCommissionDTO,
      ): Promise<CommissionDTO> {
        return fetcher.patch<CommissionDTO>(
          `${config.apiUrl}/events/${eventId}/commissions/${commissionId}`,
          input,
        );
      },

      async markPaid(
        eventId: string,
        commissionId: string,
      ): Promise<CommissionDTO> {
        return fetcher.post<CommissionDTO>(
          `${config.apiUrl}/events/${eventId}/commissions/${commissionId}/mark-paid`,
          {},
        );
      },

      async cancel(
        eventId: string,
        commissionId: string,
      ): Promise<CommissionDTO> {
        return fetcher.post<CommissionDTO>(
          `${config.apiUrl}/events/${eventId}/commissions/${commissionId}/cancel`,
          {},
        );
      },

      async remove(
        eventId: string,
        commissionId: string,
      ): Promise<{ deleted: boolean }> {
        return fetcher.delete<{ deleted: boolean }>(
          `${config.apiUrl}/events/${eventId}/commissions/${commissionId}`,
        );
      },

      printUrl(
        eventId: string,
        commissionId: string,
        lang: BrandPrintLanguage = "lo",
      ): string {
        return withBrandPrintLanguage(
          `${config.apiUrl}/events/${eventId}/commissions/${commissionId}/print`,
          lang,
        );
      },
    },

    outsources: {
      async list(eventId: string): Promise<EventOutsourceDTO[]> {
        return fetcher.get<EventOutsourceDTO[]>(
          `${config.apiUrl}/events/${eventId}/outsources`,
        );
      },

      async create(
        eventId: string,
        input: CreateEventOutsourceDTO,
      ): Promise<EventOutsourceDTO> {
        return fetcher.post<EventOutsourceDTO>(
          `${config.apiUrl}/events/${eventId}/outsources`,
          input,
        );
      },

      async bulkCreateFromStaff(
        eventId: string,
        input: BulkCreateEventOutsourcesFromStaffDTO,
      ): Promise<BulkCreateEventOutsourcesFromStaffResultDTO> {
        return fetcher.post<BulkCreateEventOutsourcesFromStaffResultDTO>(
          `${config.apiUrl}/events/${eventId}/outsources/bulk-from-staff`,
          input,
        );
      },

      async update(
        eventId: string,
        rowId: string,
        input: UpdateEventOutsourceDTO,
      ): Promise<EventOutsourceDTO> {
        return fetcher.patch<EventOutsourceDTO>(
          `${config.apiUrl}/events/${eventId}/outsources/${rowId}`,
          input,
        );
      },

      async remove(
        eventId: string,
        rowId: string,
      ): Promise<{ deleted: boolean }> {
        return fetcher.delete<{ deleted: boolean }>(
          `${config.apiUrl}/events/${eventId}/outsources/${rowId}`,
        );
      },

      async createPayout(
        eventId: string,
        rowId: string,
        input: CreateOutsourcePayoutDTO,
      ): Promise<{ payout: PayoutDTO; warnings: unknown[] }> {
        return fetcher.post<{ payout: PayoutDTO; warnings: unknown[] }>(
          `${config.apiUrl}/events/${eventId}/outsources/${rowId}/payouts`,
          input,
        );
      },

      async saveInstallmentPlan(
        eventId: string,
        rowId: string,
        input: SaveEventOutsourceInstallmentPlanDTO,
      ): Promise<{ installments: EventOutsourceInstallmentDTO[] }> {
        return fetcher.put<{ installments: EventOutsourceInstallmentDTO[] }>(
          `${config.apiUrl}/events/${eventId}/outsources/${rowId}/installment-plan`,
          input,
        );
      },

      async adminRecalcAgreedAmount(
        eventId: string,
        rowId: string,
        input: AdminRecalcEventOutsourceAgreedAmountDTO,
      ): Promise<{ installments: EventOutsourceInstallmentDTO[] }> {
        return fetcher.patch<{ installments: EventOutsourceInstallmentDTO[] }>(
          `${config.apiUrl}/events/${eventId}/outsources/${rowId}/agreed-amount-admin`,
          input,
        );
      },
    },

    productionCosts: {
      async list(eventId: string): Promise<ProductionCostListResponseDTO> {
        return fetcher.get<ProductionCostListResponseDTO>(
          `${config.apiUrl}/events/${eventId}/production-costs`,
        );
      },

      async create(
        eventId: string,
        input: CreateProductionCostLineDTO,
      ): Promise<ProductionCostLineDTO> {
        return fetcher.post<ProductionCostLineDTO>(
          `${config.apiUrl}/events/${eventId}/production-costs`,
          input,
        );
      },

      async update(
        eventId: string,
        lineId: string,
        input: UpdateProductionCostLineDTO,
      ): Promise<ProductionCostLineDTO> {
        return fetcher.patch<ProductionCostLineDTO>(
          `${config.apiUrl}/events/${eventId}/production-costs/${lineId}`,
          input,
        );
      },

      async markPaid(
        eventId: string,
        lineId: string,
      ): Promise<ProductionCostLineDTO> {
        return fetcher.post<ProductionCostLineDTO>(
          `${config.apiUrl}/events/${eventId}/production-costs/${lineId}/mark-paid`,
          {},
        );
      },

      async cancel(
        eventId: string,
        lineId: string,
      ): Promise<ProductionCostLineDTO> {
        return fetcher.post<ProductionCostLineDTO>(
          `${config.apiUrl}/events/${eventId}/production-costs/${lineId}/cancel`,
          {},
        );
      },

      async remove(
        eventId: string,
        lineId: string,
      ): Promise<{ deleted: boolean }> {
        return fetcher.delete<{ deleted: boolean }>(
          `${config.apiUrl}/events/${eventId}/production-costs/${lineId}`,
        );
      },
    },
  },

  documents: {
    async list(): Promise<DocumentListItemDTO[]> {
      return fetcher.get<DocumentListItemDTO[]>(`${config.apiUrl}/documents`);
    },

    async get(documentId: string): Promise<DocumentDTO> {
      return fetcher.get<DocumentDTO>(
        `${config.apiUrl}/documents/${documentId}`,
      );
    },

    printUrl(
      documentId: string,
      lang: BrandPrintLanguage = "lo",
      options?: {
        includeSignature?: boolean;
        includeStudioSignature?: boolean;
        includeStudioStamp?: boolean;
      },
    ): string {
      return withBrandPrintLanguage(
        `${config.apiUrl}/documents/${documentId}/print`,
        lang,
        options,
      );
    },

    pdfUrl(documentId: string): string {
      return `${config.apiUrl}/documents/${documentId}/pdf`;
    },

    lines: {
      async create(
        documentId: string,
        input: CreateManualLineDTO,
      ): Promise<DocumentLineDTO> {
        return fetcher.post<DocumentLineDTO>(
          `${config.apiUrl}/documents/${documentId}/lines`,
          input,
        );
      },

      async update(
        documentId: string,
        lineId: string,
        input: UpdateManualLineDTO,
      ): Promise<DocumentLineDTO> {
        return fetcher.patch<DocumentLineDTO>(
          `${config.apiUrl}/documents/${documentId}/lines/${lineId}`,
          input,
        );
      },

      async remove(
        documentId: string,
        lineId: string,
      ): Promise<{ deleted: boolean }> {
        return fetcher.delete<{ deleted: boolean }>(
          `${config.apiUrl}/documents/${documentId}/lines/${lineId}`,
        );
      },
    },
  },

  payouts: {
    async list(): Promise<PayoutListItemDTO[]> {
      return fetcher.get<PayoutListItemDTO[]>(`${config.apiUrl}/payouts`);
    },

    async get(payoutId: string): Promise<PayoutDTO> {
      return fetcher.get<PayoutDTO>(`${config.apiUrl}/payouts/${payoutId}`);
    },

    async update(payoutId: string, input: UpdatePayoutDTO): Promise<PayoutDTO> {
      return fetcher.patch<PayoutDTO>(
        `${config.apiUrl}/payouts/${payoutId}`,
        input,
      );
    },

    async submit(payoutId: string): Promise<PayoutDTO> {
      return fetcher.post<PayoutDTO>(
        `${config.apiUrl}/payouts/${payoutId}/submit`,
        {},
      );
    },

    async approve(payoutId: string): Promise<PayoutDTO> {
      return fetcher.post<PayoutDTO>(
        `${config.apiUrl}/payouts/${payoutId}/approve`,
        {},
      );
    },

    async reject(payoutId: string): Promise<PayoutDTO> {
      return fetcher.post<PayoutDTO>(
        `${config.apiUrl}/payouts/${payoutId}/reject`,
        {},
      );
    },

    async markPaid(
      payoutId: string,
      input: MarkPayoutPaidDTO = {},
    ): Promise<PayoutDTO> {
      return fetcher.post<PayoutDTO>(
        `${config.apiUrl}/payouts/${payoutId}/mark-paid`,
        input,
      );
    },

    async setPaymentProof(
      payoutId: string,
      input: SetPayoutPaymentProofDTO,
    ): Promise<PayoutDTO> {
      return fetcher.put<PayoutDTO>(
        `${config.apiUrl}/payouts/${payoutId}/payment-proof`,
        input,
      );
    },

    async cancel(
      payoutId: string,
      input: CancelPayoutDTO = {},
    ): Promise<PayoutDTO> {
      return fetcher.post<PayoutDTO>(
        `${config.apiUrl}/payouts/${payoutId}/cancel`,
        input,
      );
    },

    printUrl(payoutId: string, lang: BrandPrintLanguage = "lo"): string {
      return withBrandPrintLanguage(
        `${config.apiUrl}/payouts/${payoutId}/print`,
        lang,
      );
    },

    pdfUrl(payoutId: string): string {
      return `${config.apiUrl}/payouts/${payoutId}/pdf`;
    },

    paymentProofUrl(payoutId: string): string {
      return `${config.apiUrl}/payouts/${payoutId}/payment-proof`;
    },

    async pricingInsights(payoutId: string): Promise<{
      items: Array<{
        assignmentId: string;
        roleFeeName: string | null;
        roleFeeKind: string | null;
        breakdown: Array<{ label: string; amount: number }>;
        warnings: Array<{ code: string; message: string }>;
      }>;
      warnings: Array<{
        assignmentId: string;
        performerId?: string;
        code: string;
        message: string;
      }>;
    }> {
      return fetcher.get(
        `${config.apiUrl}/payouts/${payoutId}/pricing-insights`,
      );
    },
  },

  dashboard: {
    async get(): Promise<PerformanceOperationsDashboardDTO> {
      return fetcher.get<PerformanceOperationsDashboardDTO>(
        `${config.apiUrl}/dashboard`,
      );
    },
  },

  taxSettings: {
    async get(): Promise<TaxSettingsDTO> {
      return fetcher.get<TaxSettingsDTO>(`${config.apiUrl}/tax-settings`);
    },

    async update(input: UpdateTaxSettingsDTO): Promise<TaxSettingsDTO> {
      return fetcher.patch<TaxSettingsDTO>(
        `${config.apiUrl}/tax-settings`,
        input,
      );
    },
  },

  reports: {
    async getSummary(
      query: PerformanceReportSummaryQueryDTO,
    ): Promise<PerformanceReportSummaryDTO> {
      return fetcher.get<PerformanceReportSummaryDTO>(
        buildReportUrl(`${config.apiUrl}/reports/summary`, query),
      );
    },

    async getEvents(
      query: PerformanceEventReportQueryDTO,
    ): Promise<PerformanceEventReportDTO> {
      return fetcher.get<PerformanceEventReportDTO>(
        buildReportUrl(`${config.apiUrl}/reports/events`, query),
      );
    },

    async getPayouts(
      query: PerformancePayoutReportQueryDTO,
    ): Promise<PerformancePayoutReportDTO> {
      return fetcher.get<PerformancePayoutReportDTO>(
        buildReportUrl(`${config.apiUrl}/reports/payouts`, query),
      );
    },

    async getRevenue(
      query: PerformanceRevenueReportQueryDTO,
    ): Promise<PerformanceRevenueReportDTO> {
      return fetcher.get<PerformanceRevenueReportDTO>(
        buildReportUrl(`${config.apiUrl}/reports/revenue`, query),
      );
    },

    async getRequests(
      query: PerformanceRequestReportQueryDTO,
    ): Promise<PerformanceRequestReportDTO> {
      return fetcher.get<PerformanceRequestReportDTO>(
        buildReportUrl(`${config.apiUrl}/reports/requests`, query),
      );
    },

    async getPerformers(
      query: PerformancePerformerReportQueryDTO,
    ): Promise<PerformancePerformerReportDTO> {
      return fetcher.get<PerformancePerformerReportDTO>(
        buildReportUrl(`${config.apiUrl}/reports/performers`, query),
      );
    },
  },
};
